import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Typography,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { getTrimesterEvaluationCourses } from "../../../../services/courses/courses.service";
import {
  CourseEvaluation,
  TrimesterEvaluationCourse,
} from "../../../../services/courses/courses.types";
import { ProgramEntity } from "../../../../services/programs/programs.types";
import { LessonMinimalResponse } from "../../../../types/api/getLesson";
import { evaluationQuestionTitle, TrimesterEvaluationState } from "../utils";
import CourseLessonsSelector from "./CourseLessonsSelector";

const { Text, Title } = Typography;

interface LessonSelected {
  id: number;
  title: string;
  _uid?: string;
}

interface EvaluationCourseFormProps {
  value: TrimesterEvaluationState;
  onChange: React.Dispatch<React.SetStateAction<TrimesterEvaluationState>>;
  subjectId?: number;
  programs: ProgramEntity[];

  selectedLessons: LessonSelected[];
  availableLessons: LessonMinimalResponse[];
  handleRemoveLesson: (index: number) => void;
  handleAddLessonAtIndex: (index: number) => void;
  handleLessonChange: (index: number, field: string, value: any) => void;
  handleReorderLessons: (lessons: LessonSelected[]) => void;
}

interface Choice {
  programId?: number;
  trimester?: number;
}

const TRIMESTER_OPTIONS = [1, 2, 3].map((trimester) => ({
  value: trimester,
  label: `Trimestre ${trimester}`,
}));

// `week` is the raw text of the program, so it can be a range ("4-5").
const weekLabel = (week?: string | null) => {
  const text = week?.trim();
  if (!text) return "";
  return /^\d+$/.test(text) ? `Semana ${text}` : `Semanas ${text}`;
};

// One question per returned course, in the returned order. A course that
// already has a question keeps its id and points, so saving upserts it
// instead of creating a duplicate. With `keepOthers`, questions whose course
// was not returned stay at the end.
const mergeQuestions = (
  courses: TrimesterEvaluationCourse[],
  current: CourseEvaluation[],
  keepOthers: boolean,
): CourseEvaluation[] => {
  const existing = new Map(
    current.map((question) => [question.related_course_id, question]),
  );
  const merged = courses.map(({ course_id, title, week }) => {
    const kept = existing.get(course_id);
    const course = { id: course_id, title };
    return kept
      ? { ...kept, related_course: course, related_week: week }
      : {
          related_course_id: course_id,
          related_course: course,
          related_week: week,
          max_points: 1,
        };
  });
  if (!keepOthers) return merged;

  const returned = new Set(courses.map((course) => course.course_id));
  return [
    ...merged,
    ...current.filter((question) => !returned.has(question.related_course_id!)),
  ];
};

const EvaluationCourseForm: React.FC<EvaluationCourseFormProps> = ({
  value,
  onChange,
  subjectId,
  programs,
  selectedLessons,
  availableLessons,
  handleRemoveLesson,
  handleAddLessonAtIndex,
  handleLessonChange,
  handleReorderLessons,
}) => {
  // React 19 does not render the static Modal.confirm; same as the other forms.
  const [modal, modalContextHolder] = Modal.useModal();
  // The choice whose courses are loading. The selects show it at once, but the
  // form keeps the previous program, trimester and questions until they arrive.
  const [pending, setPending] = useState<Choice | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [noCourses, setNoCourses] = useState(false);
  const latestRequest = useRef(0);

  // A response that lands after the form is gone must not be committed.
  useEffect(
    () => () => {
      latestRequest.current += 1;
    },
    [],
  );

  const shown: Choice = pending ?? {
    programId: value.programId,
    trimester: value.trimester,
  };
  const hasChoice =
    value.programId !== undefined && value.trimester !== undefined;

  const programOptions = programs
    .filter(
      (program) => (program.subject?.id ?? program.subject_id) === subjectId,
    )
    .map((program) => {
      const grade = program.grade?.grade;
      return {
        value: program.id,
        label:
          grade && !program.title.includes(grade)
            ? `${program.title} - ${grade}`
            : program.title,
      };
    });

  const totalPoints = value.questions.reduce(
    (sum, question) => sum + (question.max_points ?? 0),
    0,
  );

  const load = async (
    programId: number,
    trimester: number,
    replace: boolean,
  ) => {
    const request = ++latestRequest.current;
    setPending({ programId, trimester });
    setLoadError(false);
    try {
      const courses = await getTrimesterEvaluationCourses(programId, trimester);
      if (request !== latestRequest.current) return;
      setNoCourses(courses.length === 0);
      onChange((current) => ({
        programId,
        trimester,
        questions: mergeQuestions(courses, current.questions, !replace),
      }));
    } catch {
      if (request === latestRequest.current) setLoadError(true);
    } finally {
      if (request === latestRequest.current) setPending(null);
    }
  };

  const choose = (change: Choice) => {
    const { programId, trimester } = { ...shown, ...change };
    if (programId === undefined || trimester === undefined) {
      onChange((current) => ({ ...current, ...change }));
      return;
    }

    const replace =
      programId !== value.programId || trimester !== value.trimester;
    // Saved questions carry the teachers' captures: replacing them hides those.
    if (replace && value.questions.some((question) => question.id != null)) {
      modal.confirm({
        title: "¿Cambiar el programa o el trimestre?",
        content:
          "Al guardar se reemplazarán las preguntas actuales del curso y las capturas ya registradas por profesores dejarán de mostrarse.",
        okText: "Cambiar",
        cancelText: "Cancelar",
        onOk: () => load(programId, trimester, replace),
      });
      return;
    }
    load(programId, trimester, replace);
  };

  const updateQuestions = (
    update: (questions: CourseEvaluation[]) => CourseEvaluation[],
  ) =>
    onChange((current) => ({
      ...current,
      questions: update(current.questions),
    }));

  return (
    <div>
      {modalContextHolder}
      <Form.Item label="Programa" htmlFor="evaluation-program">
        <Select
          id="evaluation-program"
          showSearch
          optionFilterProp="label"
          placeholder="Selecciona un programa"
          disabled={!subjectId}
          loading={pending !== null}
          value={shown.programId}
          options={programOptions}
          style={{ width: "100%" }}
          // onSelect, not onChange: it also fires when the same option is
          // picked again, which reloads the courses and adds the missing ones.
          onSelect={(programId: number) => choose({ programId })}
        />
      </Form.Item>
      <Form.Item label="Trimestre" htmlFor="evaluation-trimester">
        <Select
          id="evaluation-trimester"
          placeholder="Selecciona un trimestre"
          loading={pending !== null}
          value={shown.trimester}
          options={TRIMESTER_OPTIONS}
          style={{ width: "100%" }}
          onSelect={(trimester: number) => choose({ trimester })}
        />
      </Form.Item>

      {loadError && (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los cursos del trimestre. Intenta de nuevo."
          style={{ marginBottom: 16 }}
        />
      )}

      {hasChoice ? (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 16,
            }}
          >
            <Title level={4} style={{ marginBottom: 0 }}>
              Configuración de preguntas
            </Title>
            <Text type="secondary">
              {value.questions.length}{" "}
              {value.questions.length === 1 ? "pregunta" : "preguntas"} ·{" "}
              {totalPoints} pts totales
            </Text>
          </div>

          {noCourses && value.questions.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No hay cursos en este trimestre para el programa seleccionado."
            />
          )}

          {value.questions.map((question) => {
            const title = evaluationQuestionTitle(question);
            const courseId = question.related_course_id;
            return (
              <div
                key={courseId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  padding: 12,
                  borderBottom: "1px solid var(--bo-card-border)",
                }}
              >
                <Text type="secondary" style={{ minWidth: 100 }}>
                  {weekLabel(question.related_week)}
                </Text>
                <Text style={{ flex: 1, minWidth: 160 }}>{title}</Text>
                <InputNumber
                  aria-label={`Puntos de ${title}`}
                  min={1}
                  precision={0}
                  value={question.max_points ?? undefined}
                  onChange={(points) =>
                    updateQuestions((questions) =>
                      questions.map((item) =>
                        item.related_course_id === courseId
                          ? { ...item, max_points: points }
                          : item,
                      ),
                    )
                  }
                  style={{ width: 80 }}
                />
                <Text type="secondary">pts</Text>
                <Popconfirm
                  title="¿Eliminar esta pregunta?"
                  description="Esta acción no se puede deshacer"
                  onConfirm={() =>
                    updateQuestions((questions) =>
                      questions.filter(
                        (item) => item.related_course_id !== courseId,
                      ),
                    )
                  }
                  okText="Sí"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Eliminar pregunta de ${title}`}
                  />
                </Popconfirm>
              </div>
            );
          })}
        </div>
      ) : (
        pending === null && (
          <Text type="secondary">
            Selecciona un programa y un trimestre para precargar las preguntas.
          </Text>
        )
      )}

      <div style={{ marginTop: 24 }}>
        <CourseLessonsSelector
          selectedLessons={selectedLessons}
          availableLessons={availableLessons}
          handleRemoveLesson={handleRemoveLesson}
          handleAddLessonAtIndex={handleAddLessonAtIndex}
          handleLessonChange={handleLessonChange}
          handleReorderLessons={handleReorderLessons}
        />
      </div>
    </div>
  );
};

export default EvaluationCourseForm;
